import { CredentialContextVersion } from '@trustvc/w3c-context';
import { assertStatusList2021Entry, assertBitstringStatusListEntry, assertTransferableRecords, assertCredentialStatusType } from '@trustvc/w3c-credential-status';
import { VerificationType } from '@trustvc/w3c-issuer';
import { createHash } from 'crypto';
import * as jsonld from 'jsonld';
import { v7 } from 'uuid';
import { assertCredentialStatuses } from '../sign/credentialStatus';
import { proofTypeMapping } from '../types';

var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });
function _checkKeyPair(keyPair) {
  if (!keyPair.controller) {
    throw new Error('"controller" property in keyPair is required.');
  }
  if (!keyPair.id) {
    throw new Error('"id" property in keyPair is required.');
  }
  if (keyPair.type === VerificationType.Bls12381G2Key2020) {
    if (!keyPair.privateKeyBase58) {
      throw new Error('"privateKeyBase58" property in keyPair is required.');
    }
    if (!keyPair.publicKeyBase58) {
      throw new Error('"publicKeyBase58" property in keyPair is required.');
    }
  } else if (keyPair.type === VerificationType.Multikey) {
    const multikeyPair = keyPair;
    if (!multikeyPair.secretKeyMultibase) {
      throw new Error('"secretKeyMultibase" property in keyPair is required.');
    }
    if (!multikeyPair.publicKeyMultibase) {
      throw new Error('"publicKeyMultibase" property in keyPair is required.');
    }
  }
}
__name(_checkKeyPair, "_checkKeyPair");
function _getId(obj) {
  if (typeof obj === "string") {
    return obj;
  }
  if (!("id" in obj)) {
    return;
  }
  return obj.id;
}
__name(_getId, "_getId");
const mustHaveType = [
  "proof",
  "credentialStatus",
  "credentialSchema",
  "termsOfUse",
  "refreshService",
  "evidence"
];
const dateRegex = new RegExp("-?([1-9][0-9]{3,}|0[0-9]{3})-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])T(([01][0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9](.[0-9]+)?|(24:00:00(.0+)?))(Z|(\\+|-)((0[0-9]|1[0-3]):[0-5][0-9]|14:00))?");
function assertDateString({ credential, prop }) {
  const value = credential[prop];
  if (!dateRegex.test(value)) {
    throw new Error(`"${prop}" must be a valid date: ${value}`);
  }
}
__name(assertDateString, "assertDateString");
function getFirstContext(credential) {
  const v = jsonld.getValues(credential, "@context");
  return Array.isArray(v) && v.length ? v[0] : credential["@context"];
}
__name(getFirstContext, "getFirstContext");
function assertCredentialContext(credential) {
  const firstContext = getFirstContext(credential);
  if (firstContext !== CredentialContextVersion.v1 && firstContext !== CredentialContextVersion.v2) {
    throw new Error(`The first element of '@context' must be either '${CredentialContextVersion.v1}' (v1.1) or '${CredentialContextVersion.v2}' (v2.0).`);
  }
}
__name(assertCredentialContext, "assertCredentialContext");
function _checkCredential(credential, now = /* @__PURE__ */ new Date(), mode = "verify") {
  if (typeof now === "string") {
    now = new Date(now);
  }
  if (jsonld.getValues(credential, "@context").length < 1) {
    throw new Error('"@context" property is required.');
  }
  assertCredentialContext(credential);
  if (!credential.type) {
    throw new Error('"type" property is required.');
  }
  if (!jsonld.getValues(credential, "type").includes("VerifiableCredential")) {
    throw new Error('"type" must include `VerifiableCredential`.');
  }
  _checkCredentialSubjects(credential);
  if (!credential.issuer) {
    throw new Error('"issuer" property is required.');
  }
  if (jsonld.getValues(credential, "issuer").length > 1) {
    throw new Error('"issuer" property can only have one value.');
  }
  if ("issuer" in credential) {
    const issuer = _getId(credential.issuer);
    if (!issuer) {
      throw new Error(`"issuer" id is required.`);
    }
    _validateUriId({
      id: issuer,
      propertyName: "issuer"
    });
  }
  const firstContext = getFirstContext(credential);
  const isV2 = firstContext === CredentialContextVersion.v2;
  if (isV2) {
    _checkCredentialSchemas(credential);
    if ("validFrom" in credential) {
      assertDateString({
        credential,
        prop: "validFrom"
      });
      if (jsonld.getValues(credential, "validFrom").length > 1) {
        throw new Error('"validFrom" property can only have one value.');
      }
    }
    if ("validUntil" in credential) {
      assertDateString({
        credential,
        prop: "validUntil"
      });
      if (jsonld.getValues(credential, "validUntil").length > 1) {
        throw new Error('"validUntil" property can only have one value.');
      }
      if (mode === "verify") {
        if (now > new Date(credential.validUntil)) {
          console.warn("Credential has expired.");
        }
      }
    }
    if ("validFrom" in credential && "validUntil" in credential) {
      const validFromDate = new Date(credential.validFrom);
      const validUntilDate = new Date(credential.validUntil);
      if (validFromDate > validUntilDate) {
        throw new Error("validFrom must be temporally the same or earlier than validUntil");
      }
    }
    if (mode === "verify" && credential.validFrom) {
      const validFromDate = new Date(credential.validFrom);
      if (now < validFromDate) {
        throw new Error(`The current date time (${now.toISOString()}) is before the "validFrom" (${credential.validFrom}).`);
      }
    }
  } else {
    if (!credential.issuanceDate) {
      throw new Error('"issuanceDate" property is required.');
    }
    assertDateString({
      credential,
      prop: "issuanceDate"
    });
    if (jsonld.getValues(credential, "issuanceDate").length > 1) {
      throw new Error('"issuanceDate" property can only have one value.');
    }
    if ("expirationDate" in credential) {
      assertDateString({
        credential,
        prop: "expirationDate"
      });
      if (mode === "verify") {
        if (now > new Date(credential.expirationDate)) {
          console.warn("Credential has expired.");
        }
      }
    }
    if (mode === "verify") {
      const issuanceDate = new Date(credential.issuanceDate);
      if (now < issuanceDate) {
        throw new Error(`The current date time (${now.toISOString()}) is before the "issuanceDate" (${credential.issuanceDate}).`);
      }
    }
  }
  if (mode === "verify") {
    if (!credential.proof) {
      throw new Error('"proof" property is required.');
    }
    if (jsonld.getValues(credential, "proof").length > 1) {
      throw new Error('"proof" property can only have one value.');
    }
    const proofType = jsonld.getValues(credential, "proof")[0].type;
    if (!proofTypeMapping[proofType]) {
      throw new Error('"proof" type is not of BbsBlsSignature2020.');
    }
  }
  if (mode === "sign") {
    if (credential.proof) {
      throw new Error('"proof" property is already there.');
    }
    const isStatusListCredential = credential.type?.some((type) => type === "BitstringStatusListCredential" || type === "StatusList2021Credential");
    if (credential.id && !isStatusListCredential) {
      throw new Error('"id" is a defined field and should not be set by the user.');
    }
  }
  assertCredentialStatuses(credential, mode);
  for (const prop of mustHaveType) {
    if (prop in credential) {
      const _value = credential[prop];
      if (Array.isArray(_value)) {
        _value.forEach((entry) => _checkTypedObject(entry, prop));
        continue;
      }
      _checkTypedObject(_value, prop);
    }
  }
}
__name(_checkCredential, "_checkCredential");
function _checkTypedObject(obj, name) {
  if (!isObject(obj)) {
    throw new Error(`property "${name}" must be an object.`);
  }
  if (_emptyObject(obj)) {
    throw new Error(`property "${name}" can not be an empty object.`);
  }
  if (!("type" in obj)) {
    throw new Error(`property "${name}" must have property type.`);
  }
}
__name(_checkTypedObject, "_checkTypedObject");
function _checkCredentialSubjects(credential) {
  if (!credential?.credentialSubject) {
    throw new Error('"credentialSubject" property is required.');
  }
  if (Array.isArray(credential?.credentialSubject)) {
    credential?.credentialSubject.map((subject) => _checkCredentialSubject(subject));
    return;
  }
  _checkCredentialSubject(credential?.credentialSubject);
}
__name(_checkCredentialSubjects, "_checkCredentialSubjects");
function _checkCredentialSubject(subject) {
  if (isObject(subject) === false) {
    throw new Error('"credentialSubject" must be a non-null object.');
  }
  if (_emptyObject(subject)) {
    throw new Error('"credentialSubject" must make a claim.');
  }
  if (subject.id) {
    _validateUriId({
      id: subject.id,
      propertyName: "credentialSubject.id"
    });
  }
}
__name(_checkCredentialSubject, "_checkCredentialSubject");
function _checkCredentialSchemas(credential) {
  if ("credentialSchema" in credential) {
    const schemas = Array.isArray(credential.credentialSchema) ? credential.credentialSchema : [
      credential.credentialSchema
    ];
    for (const schema of schemas) {
      _checkCredentialSchema(schema);
    }
  }
}
__name(_checkCredentialSchemas, "_checkCredentialSchemas");
function _checkCredentialSchema(schema) {
  if (typeof schema !== "object" || schema === null) {
    throw new Error("credentialSchema must be an object");
  }
  if (!schema.id) {
    throw new Error("credentialSchema objects must have an id property that is a URL identifying the schema file");
  }
  if (typeof schema.id !== "string") {
    throw new Error("credentialSchema id property must be a URL string");
  }
  try {
    new URL(schema.id);
  } catch (e) {
    throw new Error("credentialSchema id property must be a valid URL");
  }
}
__name(_checkCredentialSchema, "_checkCredentialSchema");
function isObject(obj) {
  if (obj === null) {
    return false;
  }
  if (typeof obj === "object") {
    return true;
  }
  return false;
}
__name(isObject, "isObject");
function _emptyObject(obj) {
  if (!isObject(obj)) {
    return true;
  }
  return Object.keys(obj).length === 0;
}
__name(_emptyObject, "_emptyObject");
function _validateUriId({ id, propertyName }) {
  let parsed;
  try {
    parsed = new URL(id);
  } catch (e) {
    const error = new Error(`"${propertyName}" must be a URI: "${id}".`);
    throw error;
  }
  if (!parsed.protocol) {
    throw new Error(`"${propertyName}" must be a URI: "${id}".`);
  }
}
__name(_validateUriId, "_validateUriId");
const _checkCredentialStatus = /* @__PURE__ */ __name((credentialStatus, mode = "verify") => {
  const { type } = credentialStatus ?? {};
  if (type === "StatusList2021Entry") {
    assertStatusList2021Entry(credentialStatus);
  } else if (type === "BitstringStatusListEntry") {
    assertBitstringStatusListEntry(credentialStatus);
  } else if (type === "TransferableRecords") {
    assertTransferableRecords(credentialStatus, mode);
  } else {
    assertCredentialStatusType(type);
  }
}, "_checkCredentialStatus");
const prefilCredentialId = /* @__PURE__ */ __name((credential, cryptoSuite) => {
  const isStatusListCredential = credential.type?.some((type) => type === "BitstringStatusListCredential" || type === "StatusList2021Credential");
  if (credential.id && isStatusListCredential) ; else {
    if (cryptoSuite === "ecdsa-sd-2023" || cryptoSuite === "bbs-2023") {
      credential.id = `urn:uuid:${v7()}`;
    } else {
      credential.id = `urn:bnid:_:${v7()}`;
    }
  }
  if (credential?.credentialStatus?.type === "TransferableRecords") {
    credential.credentialStatus = {
      ...credential.credentialStatus,
      tokenId: createHash("sha256").update(credential.id).digest("hex")
    };
  }
  return credential;
}, "prefilCredentialId");

export { _checkCredential, _checkCredentialStatus, _checkKeyPair, _validateUriId, getFirstContext, prefilCredentialId };
